import { clsx } from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { useCurrentUser } from '@src/hooks/useCurrentUser';

interface EmailSuppressedBarProps {
  className?: string;
}

const SUPPORT_EMAIL = 'support@bordr.com';

/**
 * Warns a customer whose email address we can no longer deliver to.
 *
 * Deliberately not dismissable: these customers miss renewal reminders and only discover the
 * problem when they are charged, so the notice stays up until support moves them to a working
 * address.
 */
export function EmailSuppressedBar({
  className,
}: EmailSuppressedBarProps): JSX.Element | null {
  const { user } = useCurrentUser();
  const { t } = useTranslation();

  if (!user?.isEmailSuppressed) {
    return null;
  }

  return (
    <div
      className={clsx(
        'flex items-center gap-x-3 bg-amber-500 px-6 py-2.5 sm:px-3.5',
        className,
      )}
      role="alert"
    >
      <HiExclamationTriangle
        aria-hidden="true"
        className="size-5 shrink-0 text-white"
      />
      <p className="text-sm/6 text-white">
        <Trans
          i18nKey="email_suppressed.notice"
          defaults="We can't deliver emails to <bold>{{email}}</bold>, so you may be missing important updates such as renewal reminders. Please contact <supportLink>{{supportEmail}}</supportLink> to update your email address."
          values={{ email: user.email, supportEmail: SUPPORT_EMAIL }}
          components={{
            bold: <span className="font-semibold" />,
            supportLink: (
              // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-semibold text-white underline"
              >
                {SUPPORT_EMAIL}
              </a>
            ),
          }}
          t={t}
        />
      </p>
    </div>
  );
}

export default EmailSuppressedBar;
